import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signInWithEmail } from '../lib/supabase';
import { THEME } from '../constants/theme';
import { useAnalytics } from '../hooks/useAnalytics';

export default function Login({ onClose }) {
  const { logEvent } = useAnalytics();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectUri = searchParams.get('redirect_uri');

  const handleRedirectToApp = (session) => {
    if (redirectUri) {
      // Construct the redirect URL with the tokens
      const redirectUrl = new URL(redirectUri);
      redirectUrl.searchParams.set('access_token', session.access_token);
      redirectUrl.searchParams.set('refresh_token', session.refresh_token);
      
      console.log('Redirecting to mobile app:', redirectUrl.toString());
      window.location.replace(redirectUrl.toString());
    } else if (onClose) {
      // If in modal context, just close the modal
      onClose();
    } else {
      // If standalone web app, navigate to dashboard
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      logEvent('User', 'Login Attempt', 'Email Login');
      const { data, error } = await signInWithEmail(email, password);
      if (error) throw error;
      
      handleRedirectToApp(data.session);
      logEvent('User', 'Login Success', 'Email Login');
    } catch (err) {
      setError(err.message);
      logEvent('User', 'Login Error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      logEvent('User', 'Login Attempt', 'Google Login');
      // ... existing Google login code ...
      // Add redirect handling after successful login
      logEvent('User', 'Login Success', 'Google Login');
    } catch (error) {
      logEvent('User', 'Login Error', error.message);
      // ... existing error handling ...
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Background Circles */}
        <div className="absolute left-0 bottom-0 w-96 h-96 bg-[#E5F5F5] rounded-full -translate-x-1/2 translate-y-1/2" />
        <div className="absolute right-0 top-0 w-96 h-96 bg-[#E5F5F5] rounded-full translate-x-1/2 -translate-y-1/2" />
        <div className="absolute right-0 top-0 w-16 h-16 text-[#E5F5F5]">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.0001 1H13.0001V4H11.0001V1ZM15.0001 2H17.0001V5H15.0001V2ZM7.00006 3H9.00006V6H7.00006V3ZM20.0001 4H22.0001V7H20.0001V4ZM3.00006 5H5.00006V8H3.00006V5Z" />
          </svg>
        </div>

        {/* Logo */}
        <div className="flex justify-center mb-12">
          <img src="/logo.png" alt="Aided" className="h-12" />
        </div>

        {/* Login Form */}
        <div>
          
        </div>
        <div className="bg-[#004D4D] rounded-2xl p-8 shadow-lg">
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-full p-4">
              <svg className="w-8 h-8 text-[#004D4D]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
              </svg>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full bg-[#006666] text-white placeholder-[#99CCCC] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#99CCCC]"
                required
              />
            </div>

            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full bg-[#006666] text-white placeholder-[#99CCCC] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#99CCCC]"
                required
              />
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            <div className="flex justify-between items-center text-sm">
              <Link to="/forgot-password" className="text-[#99CCCC] hover:text-white transition-colors">
                Forgot Password
              </Link>
              <Link 
                to={"/signup" + (redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : '')}
                state={{ onClose }} 
                className="text-[#99CCCC] hover:text-white transition-colors"
                style={{ color: THEME.primary }}
              >
                Sign Up
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#66B2B2] hover:bg-[#99CCCC] text-white rounded-lg px-4 py-3 font-medium transition-colors relative"
            >
              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>
        

        <div className="text-sm text-center">
          <span style={{ color: THEME.subtext }}>Don't have an account? </span>
          <Link 
            to="/signup" 
            state={{ onClose }} 
            className="font-medium hover:text-primary-dark"
            style={{ color: THEME.primary }}
          >
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
} 